import { formatGBP } from "@/lib/brand";
import { monthNames } from "@/lib/costHelpers";

const even = (dips) => {
  const rest = (12 - dips.reduce((a, [, v]) => a + v, 0)) / (12 - dips.length);
  const arr = Array(12).fill(Number(rest.toFixed(3)));
  dips.forEach(([k, v]) => (arr[k] = v));
  return arr;
};

// Calendar-month multipliers (Jan..Dec); each averages ~1 across the year.
export const SEASONALITY_PRESETS = {
  steady: null,
  summer_peak: [0.7, 0.7, 0.85, 1, 1.15, 1.3, 1.4, 1.3, 1.1, 0.9, 0.8, 0.7],
  winter_peak: [1.3, 1.1, 0.9, 0.8, 0.75, 0.7, 0.7, 0.8, 0.95, 1.1, 1.3, 1.5],
  q1_slow: [0.6, 0.7, 0.85, 1, 1.1, 1.15, 1.15, 1.15, 1.1, 1.05, 1.1, 1.05],
  quiet_summer: even([[6, 0.6], [7, 0.6]]),
  quiet_summer_dec: even([[6, 0.6], [7, 0.6], [11, 0.6]]),
  custom: null,
};

export const RHYTHM_OPTIONS = [
  { k: "steady", label: "Pretty steady", body: "Roughly the same each month." },
  { k: "quiet_summer", label: "Steady, quieter summer", body: "A dip in July and August." },
  { k: "quiet_summer_dec", label: "Steady, quieter summer & December", body: "Dips in July, August and December." },
  { k: "summer_peak", label: "Summer peak", body: "Busier May–September, quieter winters." },
  { k: "winter_peak", label: "Winter peak", body: "Busier October–January, slower summer." },
  { k: "q1_slow", label: "Slow Q1", body: "January–February quieter, warms up from March." },
  { k: "custom", label: "Mixed / other", body: "I'll describe it below." },
];

export const isOther = (r) => r.offer_type === "other";
export const sellMode = (r) => (isOther(r) ? (Array.isArray(r.monthly_amounts) ? "custom" : "every") : Array.isArray(r.monthly_sales) ? "custom" : r.is_future ? "from" : "every");
export const followsRhythm = (r) => sellMode(r) !== "custom";

export const streamComplete = (r) => !!(r.name || "").trim() && Number(r.price) > 0;

export function isLaunch(r) {
  const mode = sellMode(r);
  if (mode === "from") return (Number(r.launch_month_index) || 0) > 0;
  if (mode === "custom" && !isOther(r)) return (Number(r.monthly_sales?.[0]) || 0) === 0 && r.monthly_sales.some((n) => Number(n) > 0);
  return false;
}

export function launchMonthIndex(r) {
  if (sellMode(r) === "from") return Number(r.launch_month_index) || 0;
  if (Array.isArray(r.monthly_sales)) return Math.max(0, r.monthly_sales.findIndex((n) => Number(n) > 0));
  return 0;
}

// £ in forecast month i (0..11), with optional rhythm multiplier.
export function streamMonth(r, i, startIso, preset, excluded = []) {
  const price = Number(r.price) || 0;
  const mode = sellMode(r);
  if (mode === "custom") return isOther(r) ? Number(r.monthly_amounts[i]) || 0 : price * (Number(r.monthly_sales[i]) || 0);
  if (mode === "from" && i < (Number(r.launch_month_index) || 0)) return 0;
  const base = price * (isOther(r) ? 1 : Number(r.clients_per_month) || 0);
  const mult = SEASONALITY_PRESETS[preset];
  if (!mult || excluded.includes(r.id)) return base;
  const m = Number((startIso || "2026-01").split("-")[1]);
  return base * mult[(m - 1 + i) % 12];
}

export function streamHeader(r, i, startIso) {
  const name = r.name || `Income stream ${i + 1}`;
  const mode = sellMode(r);
  const price = Number(r.price) || 0;
  if (isOther(r)) {
    if (mode === "custom") {
      const active = r.monthly_amounts.filter((n) => Number(n) > 0).length;
      return `${name} · Custom · ${active} months · ${formatGBP(r.monthly_amounts.reduce((a, b) => a + (Number(b) || 0), 0))} total`;
    }
    return `${name} · Every month · ${formatGBP(price)}`;
  }
  if (mode === "custom") {
    const n = r.monthly_sales.reduce((a, b) => a + (Number(b) || 0), 0);
    return `${name} · Custom · ${n} sales · ${formatGBP(price * n)} total`;
  }
  const qty = `${Number(r.clients_per_month) || 0} × ${formatGBP(price)}`;
  if (mode === "from") return `${name} · From ${monthNames(startIso, true)[Number(r.launch_month_index) || 0]} · ${qty}`;
  return `${name} · Every month · ${qty}`;
}
